import cn from 'classnames'
import { useRouter } from 'next/router'
// import Link from 'next/link'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  IEnum_Solution_Item_Type,
  IGetSharedPortfoliosForMyOrganizationQuery,
  ISharedPortfolioMembershipItemFragment,
  ISharedPortfolioSolutionItemFragment,
  ISolutionListItemFragment,
  ISubTopicItemFragment,
  ITopicItemFragment,
  useGetSharedPortfoliosForMyOrganizationLazyQuery,
} from 'src/graphql/generated/hooks'
import { AppState } from 'src/store/types'

import { CREATE_NEW_PORTFOLIO_ID, PortfolioPickerOption } from '../global/CreateNewPortfolioModal'
import { VendorListItem } from './VendorListItem/VendorListItem'

export interface VendorlistProps {
  items: ISolutionListItemFragment[]
  topic?: ITopicItemFragment
  subTopic?: ISubTopicItemFragment
  className?: string
  companyName?: string
  isManage?: boolean
  onRefresh?: (item: boolean) => void
  isFrom?: string
  followOnly?: boolean
  refreshFollowVendorList?: () => void
  sharedPortfolioId?: string
  sharedPortfolioSlug?: string
  showPortfolioCustomTags?: boolean
  refetchSideBarTagsData?: () => void
}

type SharedPortfolioListItem =
  IGetSharedPortfoliosForMyOrganizationQuery['sharedPortfoliosForMyOrganization']['portfolios'][number]
type ISharedPortfolioMembershipListEntry = ISharedPortfolioMembershipItemFragment
type ISharedPortfolioSolutionListEntry = ISharedPortfolioSolutionItemFragment

type PortfolioRow = {
  kind: 'private' | 'shared'
  id: string
  name: string
  slug: string
  shared_portfolio_memberships?: ISharedPortfolioMembershipListEntry[]
  shared_portfolio_solutions?: ISharedPortfolioSolutionListEntry[]
  sharedPortfolioId?: string
}

export const VendorList: React.FC<VendorlistProps> = ({
  items,
  className = '',
  topic,
  subTopic,
  companyName,
  isManage,
  onRefresh,
  isFrom = '',
  followOnly = false,
  refreshFollowVendorList,
  sharedPortfolioId = undefined,
  sharedPortfolioSlug = undefined,
  showPortfolioCustomTags = false,
  refetchSideBarTagsData = () => {},
}) => {
  /* fetch page name from url and slug */
  const router = useRouter()
  const authInfo = useSelector((state: AppState) => state.authInfo)
  const hasPremiumOrganization = authInfo?.premiumOrganizationId != null && authInfo?.premiumOrganizationName != null
  // const [activeTab, setActiveTab] = useState<string>('All')
  const [isPremium, setIsPremium] = useState<boolean | null>(null)
  const [loadSharedPortfolios, { data }] = useGetSharedPortfoliosForMyOrganizationLazyQuery()
  const apiType = undefined

  useEffect(() => {
    if (
      authInfo.idToken &&
      authInfo.idToken !== null &&
      authInfo.idToken !== '' &&
      authInfo.id &&
      authInfo.id !== null
    ) {
      const premium = authInfo?.isPremium || false
      setIsPremium(premium)
    }
  }, [authInfo, router])

  const payload = data?.sharedPortfoliosForMyOrganization

  const displayRows: PortfolioRow[] = useMemo(() => {
    const rows: PortfolioRow[] = []
    const stub = payload?.privateFollowingPortfolio
    const showPrivateOnPage = stub && (apiType === 'private_portfolio' || !apiType)
    const portfoliosHavePrivateRow = (payload?.portfolios ?? []).some(
      (p) => p?.attributes && p.attributes.isSharedPortfolio === false,
    )

    if (showPrivateOnPage && !portfoliosHavePrivateRow) {
      rows.push({
        kind: 'private',
        id: stub?.documentId || 'private_portfolio',
        name: stub?.name || 'My Private Portfolio',
        slug: stub?.slug || '',
        shared_portfolio_memberships: [],
        shared_portfolio_solutions: [],
      })
    }

    if (apiType !== 'private_portfolio') {
      const list: Array<SharedPortfolioListItem | null | undefined> = payload?.portfolios ?? []
      for (const p of list) {
        const id = p?.id
        const attributes = p?.attributes
        const name = attributes?.name
        const slug = attributes?.slug
        const sharedPortfolioMemberships =
          (attributes?.shared_portfolio_memberships as ISharedPortfolioMembershipListEntry[]) ?? []
        const sharedPortfolioSolutions =
          (attributes?.shared_portfolio_solutions as ISharedPortfolioSolutionListEntry[]) ?? []
        if (id && name) {
          rows.push({
            kind: attributes?.isSharedPortfolio === false ? 'private' : 'shared',
            id: String(id),
            name: String(name),
            slug: String(slug),
            shared_portfolio_memberships: sharedPortfolioMemberships,
            shared_portfolio_solutions: sharedPortfolioSolutions,
          })
        }
      }
    }

    const sharedPart = rows.filter((r) => r.kind === 'shared')
    const privatePart = rows.filter((r) => r.kind === 'private')
    const merged = [...privatePart, ...sharedPart]

    return merged
  }, [payload, apiType])

  const portfolioPickerOptions: PortfolioPickerOption[] = useMemo(() => {
    if (!hasPremiumOrganization) {
      return []
    }

    const create: PortfolioPickerOption = {
      kind: 'create-new',
      id: CREATE_NEW_PORTFOLIO_ID,
      title: 'Create a New Portfolio',
      subtitle: 'Create a new shared solutions portfolio.',
    }
    const fromRows: PortfolioPickerOption[] = displayRows.map((r) => ({
      kind: 'portfolio',
      id: r.id,
      title: r.name,
      slug: r.slug,
      visibility: r.kind,
      subtitle: r.kind === 'private' ? 'These solutions are visible only to you.' : 'Shared with your organization.',
      shared_portfolio_memberships: r.shared_portfolio_memberships ?? [],
      shared_portfolio_solutions: r.shared_portfolio_solutions ?? [],
    }))
    return [create, ...fromRows]
  }, [displayRows, hasPremiumOrganization])

  const refetchSharedPortfolios = useCallback(async () => {
    if (!isPremium || !hasPremiumOrganization) return
    await loadSharedPortfolios({
      variables: {
        page: 1,
        pageSize: 1000,
        type: apiType,
      },
      fetchPolicy: 'network-only',
    })
  }, [isPremium, hasPremiumOrganization, apiType])

  useEffect(() => {
    if (!router.isReady) return
    void refetchSharedPortfolios()
  }, [router.isReady, refetchSharedPortfolios])

  const portfolioSlug = sharedPortfolioSlug || (router.query.slug as string) || ''
  // const pageName = router.pathname.replace(/^\//, '').split('/')[0] // Remove leading slash , // Get first segment, e.g., "my-shared-portfolios"

  return (
    <div className={cn(className, 'bg-white', 'border-y', 'border-zinc-300')}>
      <ul className="divide-y divide-zinc-300">
        {items.map((item, index) => {
          let href = ''
          if (item.type === IEnum_Solution_Item_Type.Alsp) {
            href = '/alsps/' + item.slug
          } else if (item.type === IEnum_Solution_Item_Type.Consultancy) {
            href = '/consultants/' + item.slug
          } else if (item.type === IEnum_Solution_Item_Type.Product) {
            href = '/vendors/' + item.slug
          } else if (item.type === IEnum_Solution_Item_Type.Company) {
            href = '/companies/' + item.slug
          }

          return (
            <li key={index} className="flex flex-row justify-center items-center">
              <div className="flex-1">
                <VendorListItem
                  item={item}
                  href={href}
                  topic={topic}
                  subTopic={subTopic}
                  companyName={companyName}
                  showCompare={!isManage}
                  showFollow={!isManage}
                  onRefresh={onRefresh}
                  isFrom={isFrom}
                  isManage={isManage}
                  editSolutionHref={href}
                  itemSlug={item.slug}
                  followOnly={followOnly}
                  refreshFollowVendorList={refreshFollowVendorList}
                  portfolioSlug={portfolioSlug}
                  portfolioPickerOptions={portfolioPickerOptions}
                  onSubmitSharedPortfolio={refetchSharedPortfolios}
                  sharedPortfolioId={sharedPortfolioId || undefined}
                  solutionId={item.documentId || ''}
                  showPortfolioCustomTags={showPortfolioCustomTags}
                  refetchSideBarTagsData={refetchSideBarTagsData}
                />
              </div>
              {/* {isManage && (
                <div>
                  <Link href={href + '?viewType=manage'}>
                    <button className="box-border h-[50px] w-[130px] border-solid border-[1px] border-mist rounded-full bg-navy text-white items-center justify-center font-semibold">
                      View / Edit
                    </button>
                  </Link>
                  <Link href={`/add-annoucements/${item.slug}/`}>
                    <button className="box-border h-[50px] w-[200px] border-solid border-[1px] border-mist rounded-full bg-navy text-white items-center justify-center font-semibold">
                      Add Announcement
                    </button>
                  </Link>
                </div>
              )} */}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
